import type { BillingOwedCycle } from "@/lib/billing-overview";
import { cardChangedSince } from "@/lib/card-change";
import { billingDay } from "@/lib/billing-day";

export const AUTO_DEBIT_TIMEZONE = "America/Bogota";

export interface AutoDebitPreapproval {
  status: string;
  mode?: string | null;
  card_changed_at?: string | null;
}

export interface AutoDebitRejection {
  at: string;
  reason: string | null;
}

export interface AutoDebitInput {
  preapproval: AutoDebitPreapproval | null;
  paymentMode: string | null;
  today: string;
  owedCycles: BillingOwedCycle[];
  owedTotal: number;
  nextBillingDate: string | null;
  fallbackAmount: number;
  latestRejection: AutoDebitRejection | null;
}

export type AutoDebitDueDay =
  | { state: "scheduled"; dueDate: string; amount: number }
  | { state: "rejected"; dueDate: string; reason: string | null };

const bogotaDayFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: AUTO_DEBIT_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export function bogotaDay(value: Date | string): string | null {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return null;
  return bogotaDayFormatter.format(date);
}

export function hasActiveAutoDebit(preapproval: AutoDebitPreapproval | null, paymentMode: string | null): boolean {
  return preapproval?.status === "authorized" && paymentMode !== "cash";
}

export function resolveAutoDebitDueDay(input: AutoDebitInput): AutoDebitDueDay | null {
  if (!hasActiveAutoDebit(input.preapproval, input.paymentMode) || !input.today) return null;

  const owed = input.owedCycles;
  const dueDate = owed.length > 0 ? owed[0].dueDate : billingDay(input.nextBillingDate);
  if (!dueDate || dueDate > input.today) return null;

  const rejection = input.latestRejection && !cardChangedSince(input.preapproval?.card_changed_at, input.latestRejection.at)
    ? input.latestRejection
    : null;
  const rejectedDay = rejection ? bogotaDay(rejection.at) : null;
  if (rejectedDay && rejectedDay >= dueDate) {
    return { state: "rejected", dueDate, reason: rejection?.reason ?? null };
  }

  const onlyToday = dueDate === input.today && owed.every((cycle) => cycle.dueDate === input.today);
  if (!onlyToday) return null;

  const amount = input.owedTotal > 0 ? input.owedTotal : Math.max(0, input.fallbackAmount);
  return { state: "scheduled", dueDate, amount };
}

const REJECTION_REASONS: Record<string, string> = {
  cc_rejected_insufficient_amount: "La tarjeta no tiene fondos suficientes.",
  cc_rejected_bad_filled_card_number: "Los datos de la tarjeta no son válidos.",
  cc_rejected_bad_filled_date: "La fecha de vencimiento de la tarjeta no es válida.",
  cc_rejected_bad_filled_security_code: "El código de seguridad de la tarjeta no es válido.",
  cc_rejected_bad_filled_other: "Los datos de la tarjeta no son válidos.",
  cc_rejected_call_for_authorize: "Tu banco pide que autorices el cobro.",
  cc_rejected_card_disabled: "La tarjeta está inactiva.",
  cc_rejected_card_error: "La tarjeta no pudo procesar el cobro.",
  cc_rejected_duplicated_payment: "El cobro se rechazó por estar duplicado.",
  cc_rejected_high_risk: "Mercado Pago rechazó el cobro por seguridad.",
  cc_rejected_max_attempts: "Se alcanzó el máximo de intentos con esta tarjeta.",
  cc_rejected_blacklist: "Mercado Pago no pudo procesar el cobro con esta tarjeta.",
  cc_rejected_other_reason: "Tu banco rechazó el cobro.",
};

const GENERIC_REJECTION = "Mercado Pago no pudo cobrar tu plan con la tarjeta registrada.";

export function describeAutoDebitRejection(reason: string | null): string {
  if (!reason) return GENERIC_REJECTION;
  if (REJECTION_REASONS[reason]) return REJECTION_REASONS[reason];
  return /^[a-z_]+$/.test(reason) ? GENERIC_REJECTION : reason;
}

export function formatAutoDebitAmount(amount: number): string {
  return `$${amount.toLocaleString("es-CO")}`;
}

export function autoDebitScheduledMessage(amount: number): string {
  const plan = amount > 0 ? `tu plan (${formatAutoDebitAmount(amount)})` : "tu plan";
  return `Hoy se cobra automáticamente ${plan} con tu tarjeta en Mercado Pago. No tienes que hacer nada.`;
}
